import React, { useState, useEffect } from 'react';
import { X, Shield, Eye, Edit, Trash2, UserPlus } from 'lucide-react';
import { Document } from '../../types/documents';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useSchool } from '../../contexts/SchoolContext';
import toast from 'react-hot-toast';

interface DocumentPermissionsModalProps {
  document: Document;
  onClose: () => void;
  onUpdate: (updatedDoc: Document) => void;
}

interface UserPermission {
  userId: string;
  userEmail: string;
  userName: string;
  permission: 'view' | 'edit' | 'admin';
}

export default function DocumentPermissionsModal({
  document,
  onClose,
  onUpdate
}: DocumentPermissionsModalProps) {
  const { school } = useSchool();
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPermission, setSelectedPermission] = useState<'view' | 'edit'>('view');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPermissions();
    loadUsers();
  }, [document.id, school?.id]);

  const loadPermissions = async () => {
    try {
      // Load existing permissions from document
      const perms: UserPermission[] = [];
      
      if (document.permissions) {
        for (const [userId, permission] of Object.entries(document.permissions)) {
          // Get user details
          const userDoc = await getDocs(collection(db, 'users'));
          const userSnap = userDoc.docs.find(doc => doc.id === userId);
          
          if (userSnap) {
            const userData = userSnap.data();
            perms.push({
              userId,
              userEmail: userData.email || '',
              userName: userData.fullName || userData.displayName || 'Unknown User',
              permission: permission as 'view' | 'edit' | 'admin'
            });
          }
        }
      }
      
      setPermissions(perms);
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!school?.id) return;
    
    try {
      const usersSnap = await getDocs(collection(db, 'users'));
      const users = usersSnap.docs
        .filter(doc => {
          const userData = doc.data();
          return userData.schoolId === school.id;
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleAddPermission = () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    const user = availableUsers.find(u => u.id === selectedUser);
    if (!user) return;

    // Check if user already has permissions
    if (permissions.find(p => p.userId === selectedUser)) {
      toast.error('User already has permissions');
      return;
    }

    const newPermission: UserPermission = {
      userId: selectedUser,
      userEmail: user.email,
      userName: user.fullName || user.displayName || 'Unknown',
      permission: selectedPermission
    };

    setPermissions([...permissions, newPermission]);
    setSelectedUser('');
    setSelectedPermission('view');
  };

  const handleRemovePermission = (userId: string) => {
    setPermissions(permissions.filter(p => p.userId !== userId));
  };

  const handleChangePermission = (userId: string, permission: 'view' | 'edit' | 'admin') => {
    setPermissions(permissions.map(p => 
      p.userId === userId ? { ...p, permission } : p
    ));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Convert permissions array to object format for storage
      const permissionsObj = permissions.reduce((acc, perm) => {
        acc[perm.userId] = perm.permission;
        return acc;
      }, {} as Record<string, string>);

      // Update document in Firestore
      const docRef = doc(db, 'documents', document.id);
      await updateDoc(docRef, {
        permissions: permissionsObj,
        updatedAt: new Date().toISOString()
      });

      toast.success('Permissions updated successfully');
      
      // Update the document in parent component
      onUpdate({
        ...document,
        permissions: permissionsObj
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionBadge = (permission: string) => {
    switch (permission) {
      case 'admin':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-error-light/20 text-error-dark">
            Admin
          </span>
        );
      case 'edit':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-light/20 text-warning-dark">
            Edit
          </span>
        );
      case 'view':
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-light/20 text-success-dark">
            View
          </span>
        );
    }
  };

  const getPermissionIcon = (permission: string) => {
    switch (permission) {
      case 'admin':
        return <Shield className="w-4 h-4" />;
      case 'edit':
        return <Edit className="w-4 h-4" />;
      case 'view':
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface-light rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Document Permissions</h2>
            <p className="text-sm text-text-secondary mt-1">{document.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-dark rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Add User Section */}
          <div className="mb-6 p-4 bg-background rounded-lg">
            <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Add User Permissions
            </h3>
            <div className="flex gap-2">
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              >
                <option value="">Select a user...</option>
                {availableUsers
                  .filter(u => !permissions.find(p => p.userId === u.id))
                  .map(user => (
                    <option key={user.id} value={user.id}>
                      {user.fullName || user.displayName} ({user.email})
                    </option>
                  ))}
              </select>
              <select
                value={selectedPermission}
                onChange={(e) => setSelectedPermission(e.target.value as 'view' | 'edit')}
                className="px-3 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              >
                <option value="view">View Only</option>
                <option value="edit">Can Edit</option>
              </select>
              <button
                onClick={handleAddPermission}
                disabled={!selectedUser}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>

          {/* Current Permissions */}
          <div>
            <h3 className="text-sm font-medium text-text-primary mb-3">Current Permissions</h3>
            
            {loading ? (
              <div className="text-center py-4 text-text-secondary">Loading permissions...</div>
            ) : permissions.length > 0 ? (
              <div className="space-y-2">
                {permissions.map((perm) => (
                  <div
                    key={perm.userId}
                    className="flex items-center justify-between p-3 rounded-lg bg-background 
                             hover:bg-surface-dark transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        {getPermissionIcon(perm.permission)}
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">{perm.userName}</p>
                        <p className="text-sm text-text-secondary">{perm.userEmail}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <select
                        value={perm.permission}
                        onChange={(e) => handleChangePermission(perm.userId, e.target.value as any)}
                        className="px-2 py-1 text-sm rounded border border-border-light focus:border-primary-500 
                                 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      >
                        <option value="view">View Only</option>
                        <option value="edit">Can Edit</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={() => handleRemovePermission(perm.userId)}
                        className="p-1 text-text-secondary hover:text-error-DEFAULT transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-text-secondary">
                <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No permissions set</p>
                <p className="text-sm mt-1">Add users to control document access</p>
              </div>
            )}
          </div>

          {/* Permission Levels Explanation */}
          <div className="mt-6 p-4 bg-primary-50 rounded-lg">
            <h4 className="text-sm font-medium text-text-primary mb-2">Permission Levels:</h4>
            <ul className="space-y-1 text-sm text-text-secondary">
              <li className="flex items-center gap-2">
                <Eye className="w-3 h-3" />
                <span><strong>View:</strong> Can view and download the document</span>
              </li>
              <li className="flex items-center gap-2">
                <Edit className="w-3 h-3" />
                <span><strong>Edit:</strong> Can view, download, and modify the document</span>
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-3 h-3" />
                <span><strong>Admin:</strong> Full control including delete and permission management</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border-light">
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                     transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
}